import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FilterMatchMode, FilterOperator} from 'primereact/api';
import {checkPermission} from '../../../functions/functionLibrary';
import {permissions} from '../../../config/permissions';
import {
    injectedPersonApi,
    useDeletePersonMutation,
    useGetPersonQuery,
    useUpdateDoInsertPersonMutation
} from '../../../redux/rtk/injectedPersonApi';
import {LandRegister, Parcel, Person, Process} from '../../functions/GlobalData';
import {useMapper} from '../../hooks/useMapper';
import {Button} from 'primereact/button';
import CustomDialog from '../../global/CustomDialog';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPerson, faProjectDiagram} from '@fortawesome/free-solid-svg-icons';
import {faCircleCheck, faCircleXmark} from '@fortawesome/free-regular-svg-icons';
import moment from 'moment';
import CustomDataTable, {ColumnObject} from '../../global/CustomDataTable';
import {addMappings, Mapping, MappingType, removeMapping, resetMappings} from '../../../redux/slices/MappingSlice';
import {useAppDispatch, useAppSelector} from '../../../redux/hooks';
import {classNames} from 'primereact/utils';
import {useGetPersonenTypesQuery} from '../../../redux/rtk/injectedMappingApi';
import {getColumnsProcess, getFiltersProcess} from '../../functions/GlobalData';
import {PersonOverview} from '../../globalData/person/PersonOverview';
import {LandRegisterOverview} from '../../globalData/landRegister/LandRegisterOverview';
import {ParcelOverview} from '../../globalData/parcel/ParcelOverview';
import {ProcessDialogEditForm} from './ProcessDialogEditForm';
import {useGetProcessOverviewQuery} from '../../../redux/rtk/injectedProcessApi';
import {useLazyGetParcelQuery} from '../../../redux/rtk/injectedParcelApi';
import {setSelectedFeatures} from '../../../redux/slices/GeomonitoringSlice';
import {CustomConfirmDialog} from '../../global/CustomConfirmDialog';
import {useNavigate} from 'react-router-dom';
import {exportCSV} from '../../functions/Global';
import {Menu} from 'primereact/menu';

type Props = {
    projectId: any,
    editable: boolean,
    expandable: boolean,
    hideGlobalFilter: boolean,
    showParcels: boolean,
    showPersons: boolean,
    showLandRegisters: boolean,
    showColumnFilters: boolean,
    mapping?: any,
    dataIds?: string[],
    parentId?: string,
    selectable?: boolean,
    multiSelection?: boolean,
    onSelection?: (e: Process[]) => void,
    suggestionData?: boolean,
    scrollHeight?: string,
    className?: string,
    skeletonCount?: number,
    geoData?: any,
    showHeader?: any,
    skip?: boolean,
    isDirty?: boolean
}

const DATATABLE_ID = 'processDataTable';

export const ProcessOverview = (props: Props): JSX.Element => {

    const {t} = useTranslation(['common']);

    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const menu = useRef<any>(null);

    const [values, setValues] = useState<any>([]);
    const [selectedValue, setSelectedValue] = useState<Process | undefined>(undefined);
    const [editValue, setEditValue] = useState<boolean>(false);
    const [processIds, setProcessIds] = useState([]);
    const [triggerParcel, resultParcel, lastPromiseInfoParcel] = useLazyGetParcelQuery();
    const [dataLoaded, setDataLoaded] = useState<boolean>(props.skip || false);
    const [selection, setSelection] = useState<Process[]>([]);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<any>(null);
    const [columns, setColumns] = useState<ColumnObject[]>([]);
    const [returnedData, setReturnedData] = useState<Process>();
    const [customButtons, setCustomButtons] = useState<any[]>([]);
    const [isDirty, setIsDirty] = useState<any>(props.isDirty);

    const mappedArray = useMapper(returnedData, values, 'vorgang_id');


    const {
        data: process = undefined, isLoading,
        isSuccess,
        isFetching,
        isError,
        error,
        refetch
    } = useGetProcessOverviewQuery({
        projectId: props.projectId,
        mapping: props.mapping
    }, {skip: props.skip != null ? props.skip : false});

    useEffect(() => {
        if (window.location.hash.indexOf('_') > -1 && process) {
            const hashElements = window.location.hash.split('_');
            if (hashElements.length > 2) {
                const id = hashElements[2];
                let sProcess = process.find((item: Process) => {
                    return item.vorgang_id === id;
                });
                if (sProcess) {
                    setSelectedValue(sProcess);
                    setEditValue(true);
                }
            }
        }
    }, [process]);

    useEffect(() => {
        setColumns(getColumnsProcess(t, process, props.showParcels, props.showPersons, props.showLandRegisters));
        if (process) {
            const expandedRowsTemp: any[] = [];
            const newArray = JSON.parse(JSON.stringify(process));
            if (props.expandable) {
                newArray.map((r: any) => {
                    if (r.flurstueck || r.person || r.grundbuch) {
                        r.expandableElement = (<>
                            {r.person && r.person[0] !== null && <PersonOverview
                                showParcels={false}
                                showPersons={false}
                                showLandRegisters={false}
                                showShareInfos={false}
                                showSuggestions={false}
                                hideButtons={true}
                                projectId={props.projectId}
                                mapping={{vorgang_id: r.vorgang_id}}
                                editable={false}
                                expandable={false}
                                hideGlobalFilter={false}
                                commonData={true}
                                showColumnFilters={false}
                                skeletonCount={1}
                                geoData={props.geoData}/>}
                            {r.flurstueck && r.flurstueck[0] !== null && <ParcelOverview
                                showPersons={false}
                                showLandRegisters={false}
                                showUtilisationInfo={false}
                                showShareInfos={true}
                                hideButtons={true}
                                mapping={{vorgang_id: r.vorgang_id}}
                                commonData={true}
                                projectId={props.projectId}
                                editable={false}
                                expandable={false}
                                hideGlobalFilter={false}
                                showColumnFilters={false}
                                skeletonCount={1}
                                geoData={props.geoData}
                            />}
                            {r.grundbuch && r.grundbuch[0] !== null && <LandRegisterOverview
                                showPersons={false}
                                showParcels={false}
                                showShareInfos={true}
                                hideButtons={true}
                                projectId={props.projectId}
                                mapping={{vorgang_id: r.vorgang_id}}
                                editable={false}
                                expandable={false}
                                hideGlobalFilter={false}
                                commonData={true}
                                showColumnFilters={false}
                                skeletonCount={1}
                                geoData={props.geoData}
                            />}
                        </>);

                    }
                });
                setExpandedRows(expandedRowsTemp);
            }

            for (let i = 0; i < newArray.length; i++) {
                let p = newArray[i];

                p.geburtsdatum = moment(p.geburtsdatum).format(t('common:dateformat'));
            }

            setValues(newArray);
            setDataLoaded(true);
            // setSkip(true);
        }

    }, [JSON.stringify(process)]);

    useEffect(() => {
        if (props.mapping) {
            setCustomButtons([{
                icons: [{key: null, value: null, icon: <FontAwesomeIcon icon={faProjectDiagram}/>}],
                tooltip: t('edit'),
                tooltipOptions: {position: 'top'},
                onClick: (data: any) => {
                    setIsDirty((prevState: boolean) => {
                        const url = `/project/${props.projectId}/#globalData_0_${data['vorgang_id']}`;
                        if (prevState) {
                            CustomConfirmDialog({
                                message: t('dialogs.saveChangesMessage'),
                                header: t('dialogs.saveChangesHeader'),
                                translation: t,
                                onConfirm: () => {
                                    navigate(url);
                                },
                                rejectClassName: 'float-right p-button-secondary'
                            });
                        } else {
                            navigate(url);
                        }
                        return prevState;
                    });
                },
                permitted: true,
            }]);
        } else {
            setCustomButtons([]);
        }
    }, [props.mapping]);

    if (isError) {
        console.log({error});
    }

    useEffect(() => {
        if (typeof returnedData !== 'undefined') {
            setValues(mappedArray);
        }
    }, [returnedData]);

    useEffect(() => {
        if (props.onSelection) {
            props.onSelection(selection);
        }

        let processArray: any = [];
        selection.forEach((sel: any) => {
            //  console.log(sel);
            processArray = processArray.concat(sel.id);
        });

        setProcessIds(processArray);
    }, [selection]);

    useEffect(() => {
        if (processIds.length > 0) {
            triggerParcel({
                projectId: props.projectId,
                mapping: {process_array: processIds},
                commonData: true
            });
        }

    }, [processIds]);

    useEffect(() => {
        if (resultParcel.status === 'fulfilled') {
            const pData = resultParcel.data;
            const kennzArray: any = [];
            pData.forEach((sel: any) => {
                // console.log(sel)
                kennzArray.push(sel.flstkennz);
            });
            dispatch(setSelectedFeatures(kennzArray));

        }
    }, [resultParcel]);

    const hideDialog = () => {
        setConfirmDialogVisible(false);
        setSelectedValue(undefined);
    };

    const getButtons = () => {
        const result: any[] = [];
            result.push({
                    label: t('customDataTable:tooltips.exportAsCSV'),
                    icon: 'pi pi-file-export',
                    disabled: false,
                    command: () => {
                        exportCSV(t, values, DATATABLE_ID, columns);
                    }
                });
        return result;
    };

    return (
        <>
            <div className={classNames('h-full', props.className)}>
                <CustomDataTable
                    id={DATATABLE_ID}
                    headerTitle={props.showHeader !== false ? t('globalData:process.plural') : ''}
                    header={
                        <div className={'grid'}>
                            <div className={'col'}>
                                <h4 className={'m-0'}>{t('globalData:process.plural')}</h4>
                            </div>
                            <div className={'col flex justify-content-end flex-wrap mb-auto'}>
                                <div className={'inline mx-2'}>
                                    <Menu model={getButtons()} popup ref={menu} id="popup_menu"/>
                                    <Button className={'p-button-icon-only p-button-outlined'}
                                            icon="pi pi-angle-down"
                                            type="button"
                                            onClick={(event) => {
                                                if (menu.current) {
                                                    menu.current.toggle(event);
                                                }
                                            }} aria-controls="popup_menu" aria-haspopup/>
                                </div>
                            </div>
                        </div>
                    }
                    columns={columns}
                    customButtons={customButtons}
                    editable={checkPermission(permissions.editProcess) && props.editable}
                    scrollHeight={props.scrollHeight}
                    sortable={true}
                    value={values}
                    expandable={props.expandable}
                    expandedRows={expandedRows}
                    paginator={true}
                    paginatorHideAll={true}
                    displayColumnFilter={props.showColumnFilters}
                    selectionMode={props.selectable ? 'checkbox' : undefined}
                    selection={props.selectable ? selection : undefined}
                    onSelectionChange={props.selectable ? (e) => {
                        if (props.multiSelection) {
                            setSelection(e.value);
                        } else {
                            setSelection([e.value[e.value.length - 1]]);
                        }
                    } : undefined}
                    onEditClick={(data: any) => {
                        setSelectedValue(data);
                        setEditValue(true);
                    }}
                    ableToUpdate={props.mapping ? false : checkPermission(permissions.editProcess)}
                    filters={getFiltersProcess()}
                    dataloaded={dataLoaded}
                    hideGlobalFilter={props.hideGlobalFilter}
                    skeletonCount={props.skeletonCount}
                />
            </div>
            <ProcessDialogEditForm
                projectId={props.projectId}
                selectedProcess={selectedValue}
                show={editValue}
                geoData={props.geoData}
                setShow={setEditValue}
                setSelectedProcess={setSelectedValue}
                onFinished={(data: Process) => {
                    console.log('finished', data);
                    setReturnedData(data);
                }}
            />
        </>
    );
};
